import { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Avatar, Spinner } from '@components/ui';
import TimeEntryModal from './TimeEntryModal';
import { useTaskTimeEntries, useCreateTimeEntry, useDeleteTimeEntry } from '@hooks';
import { formatDay } from '@utils/calendarDate';

export default function TimeTrackingPanel({ taskId, projectId }) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { data: timeEntriesData, isLoading } = useTaskTimeEntries(taskId);
  const createTimeEntry = useCreateTimeEntry();
  const deleteTimeEntry = useDeleteTimeEntry();

  const timeEntries = timeEntriesData?.data || [];
  const totalHours = timeEntries.reduce((sum, entry) => sum + entry.hours, 0);

  const handleCreateEntry = async (data) => {
    await createTimeEntry.mutateAsync(data);
    setIsModalOpen(false);
  };

  const handleDeleteEntry = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este registro?')) {
      await deleteTimeEntry.mutateAsync(id);
    }
  };

  const formatDate = (date) =>
    formatDay(date, {
      day: 'numeric',
      month: 'short',
    });

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase() || '?';
  };

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
            Registro de Tiempo
          </h4>
          <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0' }}>
            Total: <span style={{ fontWeight: '600', color: '#667eea' }}>{totalHours}h</span>
          </p>
        </div>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => setIsModalOpen(true)}
        >
          + Registrar
        </Button>
      </div>

      {/* Time entries list */}
      {timeEntries.length === 0 ? (
        <div
          style={{
            padding: '24px',
            textAlign: 'center',
            backgroundColor: '#f8fafc',
            borderRadius: '12px',
            border: '1px dashed #e2e8f0',
          }}
        >
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            No hay registros de tiempo
          </p>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsModalOpen(true)}
            style={{ marginTop: '12px' }}
          >
            Registrar tiempo
          </Button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {timeEntries.map((entry) => (
            <div
              key={entry.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px',
                backgroundColor: '#f8fafc',
                borderRadius: '10px',
                transition: 'background-color 0.15s',
              }}
            >
              <Avatar initials={getInitials(entry.user?.name)} size="sm" />

              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '500', color: '#1e293b' }}>
                    {entry.user?.name || 'Usuario'}
                  </span>
                  <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                    {formatDate(entry.date)}
                  </span>
                </div>
                {entry.description && (
                  <p style={{ fontSize: '12px', color: '#64748b', margin: '4px 0 0 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {entry.description}
                  </p>
                )}
              </div>

              <div
                style={{
                  padding: '4px 10px',
                  backgroundColor: '#667eea15',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#667eea',
                }}
              >
                {entry.hours}h
              </div>

              <button
                onClick={() => handleDeleteEntry(entry.id)}
                disabled={deleteTimeEntry.isPending}
                style={{
                  padding: '4px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: '#94a3b8',
                  cursor: 'pointer',
                  opacity: deleteTimeEntry.isPending ? 0.5 : 1,
                }}
                title="Eliminar"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}

      <TimeEntryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateEntry}
        projectId={projectId}
        initialTaskId={taskId}
        isLoading={createTimeEntry.isPending}
      />
    </div>
  );
}

TimeTrackingPanel.propTypes = {
  taskId: PropTypes.number.isRequired,
  projectId: PropTypes.number,
};
